import { useEffect, useState } from "react";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Cookie, Trash2, AlertTriangle, Palette, ArrowUpRight } from "lucide-react";
import { getConsent, setConsent, analyticsAvailable, trackEvent } from "../lib/analytics";
import { privacy } from "../lib/api";
import { toast } from "sonner";

export default function SettingsPage() {
  const [consent, setConsentState] = useState(() => getConsent());
  const [forgetBusy, setForgetBusy] = useState(false);

  useEffect(() => {
    const onChange = () => setConsentState(getConsent());
    window.addEventListener("consent-changed", onChange);
    return () => window.removeEventListener("consent-changed", onChange);
  }, []);

  useEffect(() => {
    if (window.location.hash === "#forget-device") {
      const el = document.getElementById("forget-device");
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
  }, []);

  function acceptAnalytics() {
    setConsent("accepted");
    setConsentState("accepted");
    toast.success("Analytics enabled");
  }
  function withdrawAnalytics() {
    setConsent("rejected");
    setConsentState("rejected");
    toast.success("Analytics disabled · cookies cleared");
  }

  async function forgetThisDevice() {
    if (!window.confirm("This will permanently delete every prescription, todo, shopping item, note, reaction and reminder tied to this device. This cannot be undone.\n\nContinue?")) return;
    const confirm2 = window.prompt('Type "DELETE" to confirm:');
    if (confirm2 !== "DELETE") { toast.warning("Cancelled — confirmation did not match"); return; }
    setForgetBusy(true);
    try {
      const res = await privacy.forgetDevice();
      const total = Object.values(res.deleted || {}).reduce((a, b) => a + b, 0);
      // Fire the event BEFORE we clear storage / reload
      trackEvent("device_forgotten", { records_deleted: total });
      try { localStorage.clear(); } catch { /* ignore */ }
      try { sessionStorage.clear(); } catch { /* ignore */ }
      toast.success(`Erased ${total} records. Reloading…`);
      setTimeout(() => { window.location.href = "/"; }, 800);
    } catch (e) {
      setForgetBusy(false);
      toast.error("Could not erase data — please try again");
    }
  }

  return (
    <div className="animate-fade-up max-w-2xl">
      <div className="mb-8">
        <span className="label-caps">Settings</span>
        <h1 className="mt-2 text-3xl sm:text-4xl lg:text-5xl font-display font-black tracking-tighter">Preferences.</h1>
        <p className="mt-2" style={{ color: "var(--text-muted)" }}>General app settings. Tool-specific settings (reminders, Google Calendar) have moved to each tool's page — e.g. <a href="/prescriptions" className="underline">Prescriptions → Reminders & Calendar</a>.</p>
      </div>

      <div className="space-y-5">
        <div className="card-tactile rounded-2xl p-6" data-testid="consent-card">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <h3 className="font-display font-bold text-lg tracking-tight flex items-center gap-2">
                <Cookie className="h-5 w-5 text-[color:var(--primary-ink)]" /> Analytics & Privacy
              </h3>
              <p className="text-sm mt-1" style={{ color: "var(--text-muted)" }}>
                {analyticsAvailable()
                  ? <>Privacy-respecting analytics (anonymised IP). Current consent: <span className="font-semibold" data-testid="consent-status">{consent === "accepted" ? "Accepted" : consent === "rejected" ? "Rejected" : "Not set"}</span>.</>
                  : <>No analytics configured on this deployment. Your device still stores preferences locally.</>}
                {" "}Read our <a href="/privacy" className="underline" data-testid="settings-privacy-link">privacy notice</a>.
              </p>
            </div>
            {analyticsAvailable() && (
              <Switch
                checked={consent === "accepted"}
                onCheckedChange={(v) => v ? acceptAnalytics() : withdrawAnalytics()}
                data-testid="consent-toggle"
              />
            )}
          </div>
          {analyticsAvailable() && consent === "accepted" && (
            <div className="mt-3">
              <Button onClick={withdrawAnalytics} variant="outline" size="sm" className="border-[color:var(--border-subtle)]" data-testid="consent-withdraw">
                Withdraw consent
              </Button>
            </div>
          )}
        </div>

        <div id="forget-device" className="card-tactile rounded-2xl p-6" data-testid="forget-device-card" style={{ borderColor: "color-mix(in srgb, var(--accent) 40%, var(--border-subtle))" }}>
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: "color-mix(in srgb, var(--accent) 12%, transparent)" }}>
              <AlertTriangle className="h-5 w-5" style={{ color: "var(--accent)" }} />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-display font-bold text-lg tracking-tight">Forget this device</h3>
              <p className="text-sm mt-1" style={{ color: "var(--text-muted)" }}>
                GDPR right to erasure. Permanently deletes <b>every</b> prescription, dose, todo, shopping item, note, reaction, caregiver subscription, Google Calendar token and push subscription linked to this device. Your browser's local preferences (theme, favourites, pins) are also cleared. <b>This cannot be undone.</b>
              </p>
              <div className="mt-4">
                <Button
                  onClick={forgetThisDevice}
                  disabled={forgetBusy}
                  data-testid="forget-device-button"
                  className="text-white font-semibold"
                  style={{ background: "var(--accent)" }}
                >
                  <Trash2 className="h-4 w-4 mr-2" /> {forgetBusy ? "Erasing…" : "Forget this device"}
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="card-tactile rounded-2xl p-6">
          <h3 className="font-display font-bold text-lg tracking-tight">About VeriHandi</h3>
          <p className="text-sm mt-1" style={{ color: "var(--text-muted)" }}>v1.13 — Prescription Manager with Google Calendar push, weekly email digests, To-Do, Shopping, Notes, Calendar Generator, Unit Converter, QR Generator. PWA ready for iOS & Android.</p>
        </div>

        <a
          href="/settings/tokens"
          data-testid="settings-tokens-link"
          className="card-tactile rounded-2xl p-6 flex items-center gap-4 group no-underline"
        >
          <div className="h-10 w-10 rounded-xl bg-[color:var(--primary-soft)] flex items-center justify-center shrink-0">
            <Palette className="h-5 w-5 text-[color:var(--primary-ink)]" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-display font-bold text-lg tracking-tight text-[color:var(--text-main)]">Design system</h3>
            <p className="text-sm mt-0.5" style={{ color: "var(--text-muted)" }}>
              Every color, radius, shadow, and motion token VeriHandi ships with. Click-to-copy swatches. Useful for any future visual tweak.
            </p>
          </div>
          <ArrowUpRight className="h-4 w-4 text-[color:var(--text-muted)] group-hover:text-[color:var(--primary)] transition-colors shrink-0" />
        </a>
      </div>
    </div>
  );
}
